import { useState, useEffect, useRef, useCallback, useContext } from "react";
import { Coins, Users, RotateCcw, Trophy, X, Eye, Copy, Info } from "lucide-react";
import { toast } from "sonner";
import { AuthContext, API } from "../App";
import axios from "axios";

// American Roulette wheel sequence (clockwise from 0)
const WHEEL_SEQUENCE = [
  0, 28, 9, 26, 30, 11, 7, 20, 32, 17, 5, 22, 34, 15, 3, 24, 36, 13, 1,
  "00", 27, 10, 25, 29, 12, 8, 19, 31, 18, 6, 21, 33, 16, 4, 23, 35, 14, 2
];

// Number colors
const getNumberColor = (num) => {
  if (num === 0 || num === "00") return "green";
  const redNumbers = [1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36];
  return redNumbers.includes(Number(num)) ? "red" : "black";
};

// Payout rates
const PAYOUTS = {
  straight: 35,    // Single number
  split: 17,       // 2 numbers
  street: 11,      // 3 numbers (row)
  corner: 8,       // 4 numbers
  five: 6,         // 0, 00, 1, 2, 3 (American only)
  line: 5,         // 6 numbers (2 rows)
  column: 2,       // 12 numbers
  dozen: 2,        // 12 numbers
  even_money: 1,   // red/black, odd/even, 1-18/19-36
};

const RouletteGame = ({ onClose, currentUser, user: propUser, token: propToken }) => {
  const { token: authToken, user: authUser } = useContext(AuthContext);
  const effectiveToken = propToken || authToken;
  const user = propUser || authUser;
  const username = currentUser?.username || user?.username || "Player";
  
  const [balance, setBalance] = useState(user?.fchips_balance || 1000);
  const [bets, setBets] = useState([]); // Array of { type, numbers, amount }
  const [selectedChip, setSelectedChip] = useState(10);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [winnings, setWinnings] = useState(0);
  const [showRules, setShowRules] = useState(false);
  const wheelRef = useRef(null);
  const ballRef = useRef(null);

  // Fetch balance
  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const response = await axios.get(`${API}/user/balance`, {
          headers: { Authorization: `Bearer ${effectiveToken}` }
        });
        setBalance(response.data.fchips_balance || 0);
      } catch (error) {
        console.error("Failed to fetch balance:", error);
      }
    };
    if (effectiveToken) fetchBalance();
  }, [effectiveToken]);

  // Calculate total bet
  const totalBet = bets.reduce((sum, b) => sum + b.amount, 0);

  // Place a bet
  const placeBet = (type, numbers, displayName) => {
    if (spinning) return;
    if (selectedChip > balance - totalBet) {
      toast.error("Insufficient balance for this bet");
      return;
    }
    
    // Check if same bet exists, add to it
    const existingIndex = bets.findIndex(b => 
      b.type === type && JSON.stringify(b.numbers) === JSON.stringify(numbers)
    );
    
    if (existingIndex >= 0) {
      setBets(prev => prev.map((b, i) => 
        i === existingIndex ? { ...b, amount: b.amount + selectedChip } : b
      ));
    } else {
      setBets(prev => [...prev, { type, numbers, amount: selectedChip, displayName }]);
    }
  };

  // Clear all bets
  const clearBets = () => {
    if (spinning) return;
    setBets([]);
  };

  // Spin the wheel
  const spin = async () => {
    if (spinning || bets.length === 0) return;
    if (totalBet > balance) {
      toast.error("Insufficient balance");
      return;
    }

    setSpinning(true);
    setResult(null);
    setWinnings(0);

    try {
      // Call backend to process bet and get result
      const response = await axios.post(`${API}/games/roulette/spin`, {
        bets: bets.map(b => ({ type: b.type, numbers: b.numbers, amount: b.amount })),
        total_bet: totalBet
      }, {
        headers: { Authorization: `Bearer ${effectiveToken}` }
      });

      const { winning_number, total_winnings, new_balance, bet_results } = response.data;
      
      // Animate wheel
      const winIndex = WHEEL_SEQUENCE.findIndex(n => String(n) === String(winning_number));
      const segmentAngle = 360 / WHEEL_SEQUENCE.length;
      const baseRotation = 360 * 8; // 8 full rotations
      const targetAngle = baseRotation + (360 - (winIndex * segmentAngle) - segmentAngle / 2);
      
      if (wheelRef.current) {
        wheelRef.current.style.transition = 'none';
        wheelRef.current.style.transform = 'rotate(0deg)';
        
        setTimeout(() => {
          wheelRef.current.style.transition = 'transform 6s cubic-bezier(0.17, 0.67, 0.12, 0.99)';
          wheelRef.current.style.transform = `rotate(${targetAngle}deg)`;
        }, 50);
      }

      // Show result after animation
      setTimeout(() => {
        setSpinning(false);
        setResult({ number: winning_number, color: getNumberColor(winning_number) });
        setWinnings(total_winnings);
        setBalance(new_balance);
        setHistory(prev => [{ number: winning_number, color: getNumberColor(winning_number) }, ...prev.slice(0, 19)]);
        
        if (total_winnings > 0) {
          toast.success(`Winner! ${winning_number} - Won ${total_winnings} F-Chips!`);
        } else {
          toast.error(`${winning_number} - Better luck next time!`);
        }
        
        // Clear bets after showing result
        setTimeout(() => {
          setBets([]);
          setResult(null);
        }, 3000);
      }, 6000);

    } catch (error) {
      setSpinning(false);
      toast.error(error.response?.data?.detail || "Failed to spin");
    }
  };

  // Chip amounts
  const chipAmounts = [1, 5, 10, 25, 50, 100, 500];

  // Board numbers layout (3 columns x 12 rows)
  const boardRows = [];
  for (let row = 0; row < 12; row++) {
    boardRows.push([
      3 + row * 3 - 2, // Column 1: 1, 4, 7...
      3 + row * 3 - 1, // Column 2: 2, 5, 8...
      3 + row * 3,     // Column 3: 3, 6, 9...
    ].reverse()); // Reverse to match standard layout
  }

  return (
    <div className="w-full h-full bg-gradient-to-b from-green-900 via-green-800 to-green-950 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-3 bg-black/30 border-b border-yellow-600/30">
        <h2 className="text-xl font-bold text-yellow-400 flex items-center gap-2">
          🎰 AMERICAN ROULETTE
        </h2>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-black/40 px-3 py-1 rounded">
            <Coins className="w-4 h-4 text-yellow-400" />
            <span className="text-yellow-400 font-bold">{balance.toLocaleString()}</span>
          </div>
          <button onClick={() => setShowRules(true)} className="text-white/70 hover:text-white">
            <Info className="w-5 h-5" />
          </button>
          <button onClick={onClose} className="text-white hover:text-red-400">
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden p-2 gap-2">
        {/* Left: Wheel and History */}
        <div className="lg:w-1/3 flex flex-col items-center gap-2">
          {/* Wheel */}
          <div className="relative w-48 h-48 lg:w-64 lg:h-64">
            {/* Outer ring */}
            <div className="absolute inset-0 rounded-full border-4 border-yellow-600 bg-gradient-to-b from-amber-900 to-amber-950 shadow-2xl">
              {/* Wheel with numbers */}
              <div 
                ref={wheelRef}
                className="absolute inset-2 rounded-full overflow-hidden"
                style={{ transformOrigin: 'center center' }}
              >
                <svg viewBox="0 0 200 200" className="w-full h-full">
                  {WHEEL_SEQUENCE.map((num, i) => {
                    const angle = (i * 360) / WHEEL_SEQUENCE.length;
                    const color = getNumberColor(num);
                    const fillColor = color === 'red' ? '#dc2626' : color === 'black' ? '#1f2937' : '#16a34a';
                    
                    return (
                      <g key={i} transform={`rotate(${angle}, 100, 100)`}>
                        <path
                          d={`M100,100 L100,10 A90,90 0 0,1 ${100 + 90 * Math.sin(Math.PI / 19)},${100 - 90 * Math.cos(Math.PI / 19)} Z`}
                          fill={fillColor}
                          stroke="#333"
                          strokeWidth="0.5"
                        />
                        <text
                          x="100"
                          y="25"
                          textAnchor="middle"
                          fill="white"
                          fontSize="8"
                          fontWeight="bold"
                          transform={`rotate(${180 / WHEEL_SEQUENCE.length}, 100, 25)`}
                        >
                          {num}
                        </text>
                      </g>
                    );
                  })}
                  {/* Center */}
                  <circle cx="100" cy="100" r="25" fill="#1a1a1a" stroke="#fbbf24" strokeWidth="2" />
                  <circle cx="100" cy="100" r="15" fill="#333" />
                </svg>
              </div>
            </div>
            {/* Ball indicator */}
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1">
              <div className="w-0 h-0 border-l-[8px] border-r-[8px] border-t-[12px] border-l-transparent border-r-transparent border-t-yellow-400" />
            </div>
          </div>

          {/* Result display */}
          {result && (
            <div className={`text-center p-3 rounded-lg ${
              result.color === 'red' ? 'bg-red-600' : result.color === 'black' ? 'bg-gray-800' : 'bg-green-600'
            } text-white font-bold text-2xl animate-bounce`}>
              {result.number}
              {winnings > 0 && <div className="text-sm text-yellow-300">+{winnings} F-Chips!</div>}
            </div>
          )}

          {/* History */}
          <div className="w-full bg-black/30 rounded-lg p-2">
            <h3 className="text-xs text-yellow-400 mb-1">History</h3>
            <div className="flex flex-wrap gap-1">
              {history.slice(0, 15).map((h, i) => (
                <div
                  key={i}
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${
                    h.color === 'red' ? 'bg-red-600' : h.color === 'black' ? 'bg-gray-800' : 'bg-green-600'
                  }`}
                >
                  {h.number}
                </div>
              ))}
              {history.length === 0 && <span className="text-gray-500 text-xs">No spins yet</span>}
            </div>
          </div>
        </div>

        {/* Center: Betting Board */}
        <div className="flex-1 overflow-auto">
          <div className="bg-green-700 border-4 border-yellow-700 rounded-lg p-2 inline-block min-w-max">
            {/* Zero row */}
            <div className="flex mb-1">
              <button
                onClick={() => placeBet('straight', [0], '0')}
                className="w-12 h-16 bg-green-600 border border-yellow-600 text-white font-bold hover:brightness-125 transition-all flex items-center justify-center"
                disabled={spinning}
              >
                0
              </button>
              <button
                onClick={() => placeBet('straight', ['00'], '00')}
                className="w-12 h-16 bg-green-600 border border-yellow-600 text-white font-bold hover:brightness-125 transition-all flex items-center justify-center ml-1"
                disabled={spinning}
              >
                00
              </button>
            </div>

            {/* Main number grid */}
            <div className="flex">
              <div className="grid grid-cols-12 gap-0">
                {/* Numbers 1-36 in correct layout */}
                {[...Array(12)].map((_, col) => (
                  <div key={col} className="flex flex-col">
                    {[3, 2, 1].map(row => {
                      const num = col * 3 + row;
                      const color = getNumberColor(num);
                      return (
                        <button
                          key={num}
                          onClick={() => placeBet('straight', [num], String(num))}
                          className={`w-10 h-10 border border-yellow-600/50 text-white font-bold text-sm hover:brightness-125 transition-all ${
                            color === 'red' ? 'bg-red-600' : 'bg-gray-800'
                          }`}
                          disabled={spinning}
                        >
                          {num}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>

              {/* Column bets */}
              <div className="flex flex-col ml-1">
                {[3, 2, 1].map(col => (
                  <button
                    key={col}
                    onClick={() => placeBet('column', Array.from({length: 12}, (_, i) => col + i * 3), `Col ${col}`)}
                    className="w-10 h-10 bg-green-600 border border-yellow-600 text-white text-xs font-bold hover:brightness-125"
                    disabled={spinning}
                  >
                    2:1
                  </button>
                ))}
              </div>
            </div>

            {/* Dozen bets */}
            <div className="flex mt-1">
              <button
                onClick={() => placeBet('dozen', Array.from({length: 12}, (_, i) => i + 1), '1st 12')}
                className="flex-1 h-8 bg-green-600 border border-yellow-600 text-white text-xs font-bold hover:brightness-125"
                disabled={spinning}
              >
                1st 12
              </button>
              <button
                onClick={() => placeBet('dozen', Array.from({length: 12}, (_, i) => i + 13), '2nd 12')}
                className="flex-1 h-8 bg-green-600 border border-yellow-600 text-white text-xs font-bold hover:brightness-125 mx-1"
                disabled={spinning}
              >
                2nd 12
              </button>
              <button
                onClick={() => placeBet('dozen', Array.from({length: 12}, (_, i) => i + 25), '3rd 12')}
                className="flex-1 h-8 bg-green-600 border border-yellow-600 text-white text-xs font-bold hover:brightness-125"
                disabled={spinning}
              >
                3rd 12
              </button>
            </div>

            {/* Even money bets */}
            <div className="flex mt-1 gap-1">
              <button
                onClick={() => placeBet('even_money', Array.from({length: 18}, (_, i) => i + 1), '1-18')}
                className="flex-1 h-10 bg-green-600 border border-yellow-600 text-white text-xs font-bold hover:brightness-125"
                disabled={spinning}
              >
                1-18
              </button>
              <button
                onClick={() => placeBet('even_money', [2,4,6,8,10,12,14,16,18,20,22,24,26,28,30,32,34,36], 'EVEN')}
                className="flex-1 h-10 bg-green-600 border border-yellow-600 text-white text-xs font-bold hover:brightness-125"
                disabled={spinning}
              >
                EVEN
              </button>
              <button
                onClick={() => placeBet('even_money', [1,3,5,7,9,12,14,16,18,19,21,23,25,27,30,32,34,36], 'RED')}
                className="flex-1 h-10 bg-red-600 border border-yellow-600 text-white text-xs font-bold hover:brightness-125"
                disabled={spinning}
              >
                RED
              </button>
              <button
                onClick={() => placeBet('even_money', [2,4,6,8,10,11,13,15,17,20,22,24,26,28,29,31,33,35], 'BLACK')}
                className="flex-1 h-10 bg-gray-800 border border-yellow-600 text-white text-xs font-bold hover:brightness-125"
                disabled={spinning}
              >
                BLACK
              </button>
              <button
                onClick={() => placeBet('even_money', [1,3,5,7,9,11,13,15,17,19,21,23,25,27,29,31,33,35], 'ODD')}
                className="flex-1 h-10 bg-green-600 border border-yellow-600 text-white text-xs font-bold hover:brightness-125"
                disabled={spinning}
              >
                ODD
              </button>
              <button
                onClick={() => placeBet('even_money', Array.from({length: 18}, (_, i) => i + 19), '19-36')}
                className="flex-1 h-10 bg-green-600 border border-yellow-600 text-white text-xs font-bold hover:brightness-125"
                disabled={spinning}
              >
                19-36
              </button>
            </div>
          </div>
        </div>

        {/* Right: Chips and Controls */}
        <div className="lg:w-48 flex flex-col gap-2">
          {/* Chip selector */}
          <div className="bg-black/30 rounded-lg p-2">
            <h3 className="text-xs text-yellow-400 mb-2">Select Chip</h3>
            <div className="grid grid-cols-4 lg:grid-cols-2 gap-1">
              {chipAmounts.map(amount => (
                <button
                  key={amount}
                  onClick={() => setSelectedChip(amount)}
                  className={`w-10 h-10 rounded-full border-2 font-bold text-xs flex items-center justify-center transition-all ${
                    selectedChip === amount 
                      ? 'border-yellow-400 bg-yellow-600 text-black scale-110' 
                      : 'border-gray-500 bg-gray-700 text-white hover:border-yellow-400'
                  }`}
                  disabled={spinning}
                >
                  {amount}
                </button>
              ))}
            </div>
          </div>

          {/* Current bets */}
          <div className="bg-black/30 rounded-lg p-2 flex-1 overflow-auto">
            <h3 className="text-xs text-yellow-400 mb-2">Your Bets</h3>
            {bets.length === 0 ? (
              <p className="text-gray-500 text-xs">Click on the board to place bets</p>
            ) : (
              <div className="space-y-1 max-h-40 overflow-auto">
                {bets.map((bet, i) => (
                  <div key={i} className="flex justify-between text-xs text-white bg-black/30 px-2 py-1 rounded">
                    <span>{bet.displayName}</span>
                    <span className="text-yellow-400">{bet.amount}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-2 pt-2 border-t border-gray-600">
              <div className="flex justify-between text-sm font-bold text-white">
                <span>Total:</span>
                <span className="text-yellow-400">{totalBet}</span>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col gap-2">
            <button
              onClick={clearBets}
              disabled={spinning || bets.length === 0}
              className="w-full py-2 bg-red-600/80 text-white font-bold rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              CLEAR BETS
            </button>
            <button
              onClick={spin}
              disabled={spinning || bets.length === 0}
              className={`w-full py-3 font-bold rounded text-lg transition-all ${
                spinning 
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-yellow-500 to-amber-600 text-black hover:from-yellow-400 hover:to-amber-500'
              }`}
            >
              {spinning ? '🎰 SPINNING...' : '🎰 SPIN!'}
            </button>
          </div>
        </div>
      </div>

      {/* Rules Modal */}
      {showRules && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowRules(false)}>
          <div className="bg-green-900 rounded-xl p-6 max-w-lg max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold text-yellow-400 mb-4">American Roulette Rules</h2>
            <div className="text-white text-sm space-y-3">
              <div>
                <h3 className="font-bold text-yellow-300">Payouts:</h3>
                <ul className="list-disc ml-5 mt-1 text-gray-300">
                  <li><strong>Straight (single number):</strong> 35:1</li>
                  <li><strong>Column (12 numbers):</strong> 2:1</li>
                  <li><strong>Dozen (12 numbers):</strong> 2:1</li>
                  <li><strong>Red/Black, Odd/Even, 1-18/19-36:</strong> 1:1</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-yellow-300">How to Play:</h3>
                <ol className="list-decimal ml-5 mt-1 text-gray-300">
                  <li>Select a chip value</li>
                  <li>Click on the board to place bets</li>
                  <li>Click SPIN when ready</li>
                  <li>Winnings are automatically credited</li>
                </ol>
              </div>
              <p className="text-xs text-gray-400 mt-4">
                American Roulette has 38 numbers (0, 00, 1-36). 
                House edge: 5.26%
              </p>
            </div>
            <button
              onClick={() => setShowRules(false)}
              className="mt-4 w-full py-2 bg-yellow-600 text-black font-bold rounded hover:bg-yellow-500"
            >
              Got it!
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RouletteGame;
